import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Trophy, Loader2, Twitter, Linkedin, Copy, Download,
} from "lucide-react";

const COLOR_BORDER = {
  sky: "#0EA5E9", emerald: "#10B981", amber: "#F59E0B",
  violet: "#8B5CF6", rose: "#F43F5E",
};

export default function ResourceCertificate() {
  const { trackId } = useParams();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const certRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/resources/tracks/${trackId}/certificate`);
      setCert(res.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("You haven't completed this track yet.");
        nav("/app/resources", { replace: true });
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load certificate");
      }
    } finally {
      setLoading(false);
    }
  }, [trackId, nav]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !cert) {
    return (
      <div className="card p-12 text-center text-[var(--ink-2)]">
        <Loader2 size={20} className="animate-spin inline" /> Loading…
      </div>
    );
  }

  const completedDate = new Date(cert.completed_at).toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
  const borderColor = COLOR_BORDER[cert.color] || "#10B981";

  const shareText =
    `🏆 Just finished the "${cert.label}" track on iSpeedToClose — ${cert.items_count} curated wholesaling resources mastered. Bringing it home next.`;
  const shareUrl = window.location.href;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("Share text copied — paste it anywhere.");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="resource-certificate-page">
      <Link to="/app/resources" className="text-sm text-[var(--ink-2)] hover:text-[var(--primary)] inline-flex items-center gap-1 w-fit">
        <ArrowLeft size={14} /> Back to Resources
      </Link>

      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-700 font-bold">
        <Trophy size={12} /> Achievement Unlocked
      </div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight">
        Your certificate
      </h1>

      {/* The certificate — designed to be screenshot-friendly */}
      <div
        ref={certRef}
        className="relative mx-auto w-full max-w-2xl bg-white rounded-2xl overflow-hidden shadow-2xl"
        style={{ aspectRatio: "1.6 / 1", borderTop: `8px solid ${borderColor}` }}
        data-testid="resource-certificate"
      >
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 30% 20%, currentColor 1px, transparent 1.5px), radial-gradient(circle at 70% 80%, currentColor 1px, transparent 1.5px)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative h-full flex flex-col justify-between p-8 sm:p-10">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: borderColor }}>
                Certificate of Completion
              </div>
              <div className="font-display font-black text-sm tracking-tight">iSpeedToClose</div>
            </div>
            <div className="text-6xl sm:text-7xl select-none">{cert.emoji}</div>
          </div>

          {/* Body */}
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] mb-2">This certifies that</div>
            <div className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight" data-testid="cert-user-name">
              {cert.user_name}
            </div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-3)] mt-3 mb-2">has completed</div>
            <div className="font-display font-bold text-xl sm:text-2xl leading-snug" data-testid="cert-track-label">
              {cert.label}
            </div>
            <div className="text-xs text-[var(--ink-2)] mt-1">
              {cert.items_count} curated wholesaling resources
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-end justify-between gap-4 flex-wrap text-xs">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--ink-3)] mb-0.5">Completed</div>
              <div className="font-mono tabular-nums" data-testid="cert-completed-date">{completedDate}</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-widest text-[var(--ink-3)] mb-0.5">Issued by</div>
              <div className="font-display font-bold">iSpeedToClose · Tracy</div>
            </div>
          </div>
        </div>
      </div>

      {/* Share strip */}
      <div className="card p-4 mt-2 flex items-center gap-2 flex-wrap" data-testid="cert-share-strip">
        <div className="text-sm font-semibold flex-1 min-w-[200px]">
          Share the win — most wholesalers never finish a track. You did.
        </div>
        <a
          href={`https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-ghost"
          data-testid="cert-share-twitter"
        >
          <Twitter size={14} /> Tweet it
        </a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-ghost"
          data-testid="cert-share-linkedin"
        >
          <Linkedin size={14} /> LinkedIn
        </a>
        <button type="button" className="btn btn-ghost" onClick={copyShare} data-testid="cert-share-copy">
          <Copy size={14} /> Copy
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => window.print()}
          data-testid="cert-download"
          title="Use your browser's Print → Save as PDF for a clean download"
        >
          <Download size={14} /> Print / Save PDF
        </button>
      </div>
    </div>
  );
}
